import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Gavel, Check, X, PoundSterling } from "lucide-react";
import { Link } from "react-router-dom";

export default function BiddingWidget({ listing, isOwn }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [bids, setBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(false);

  const loadBids = async () => {
    if (!isOwn) return;
    setLoadingBids(true);
    try {
      const { data } = await api.get(`/bids/listing/${listing.listing_id}`);
      setBids(data.items || data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoadingBids(false);
    }
  };

  useEffect(() => {
    loadBids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.listing_id, isOwn]);

  const placeBid = async () => {
    if (!user) { toast.error("Please log in to place a bid"); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid bid amount"); return; }
    setPlacing(true);
    try {
      await api.post("/bids", { listing_id: listing.listing_id, amount: amt });
      toast.success("Bid placed");
      setAmount("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place bid");
    } finally {
      setPlacing(false);
    }
  };

  const acceptBid = async (bidId) => {
    if (!window.confirm("Accept this bid? Other pending bids will be automatically rejected.")) return;
    try {
      await api.post(`/bids/${bidId}/accept`);
      toast.success("Bid accepted. Confirm payment once the buyer has paid to finalise the sale.");
      loadBids();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept bid");
    }
  };

  const rejectBid = async (bidId) => {
    if (!window.confirm("Reject this bid?")) return;
    try {
      await api.post(`/bids/${bidId}/reject`);
      toast.success("Bid rejected");
      loadBids();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject bid");
    }
  };

  if (isOwn) {
    if (!bids.length && !loadingBids) return null;
    return (
      <div className="mt-6 border border-border bg-white p-6">
        <div className="overline text-safety mb-3 flex items-center gap-2">
          <Gavel className="w-4 h-4" /> Bids received
        </div>
        <div className="divide-y divide-border">
          {bids.map((b) => (
            <div key={b.bid_id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1 font-medium">
                <PoundSterling className="w-4 h-4" /> {Number(b.amount).toLocaleString("en-GB")}
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-muted text-muted-foreground">{b.status?.replace(/_/g, " ")}</Badge>
                {b.status === "pending" && (
                  <>
                    <Button size="sm" className="rounded-none bg-forest text-white hover:bg-forest-800" onClick={() => acceptBid(b.bid_id)}>
                      <Check className="w-4 h-4 mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => rejectBid(b.bid_id)}>
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        <Link to="/dashboard/bidding" className="mt-3 inline-block text-xs text-forest underline">Manage in Bidding Dashboard</Link>
      </div>
    );
  }

  if (listing.status !== "active") return null;

  return (
    <div className="mt-6 border border-border bg-white p-6">
      <div className="overline text-safety mb-3 flex items-center gap-2">
        <Gavel className="w-4 h-4" /> Place a dealer bid
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Requires an active Bidding Access subscription (GBP49/year).
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="1"
          step="0.01"
          placeholder="Bid amount (GBP)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="rounded-none"
          data-testid="bid-amount-input"
        />
        <Button
          onClick={placeBid}
          disabled={placing}
          className="rounded-none bg-forest hover:bg-forest-800 text-white h-10 whitespace-nowrap"
          data-testid="place-bid-btn"
        >
          {placing ? "Placing..." : "Place bid"}
        </Button>
      </div>
      <Link to="/dashboard/bidding" className="mt-2 inline-block text-xs text-forest underline">Subscribe / view my bids</Link>
    </div>
  );
}
